import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Trash2, Eye, GraduationCap, Upload, X as XIcon, Filter } from "lucide-react";
import { toast } from "sonner";
import StudentBulkUpload from "@/components/StudentBulkUpload";
import GuardianPicker from "@/components/GuardianPicker";
import { useDebounce } from "@/lib/useDebounce";

const PROGRAM_COLORS = {
  CONV: "bg-[#1B2C5A] text-white",
  CRECHE: "bg-pink-100 text-pink-800",
  ISL: "bg-emerald-100 text-emerald-800",
  TAHFEEZ: "bg-amber-100 text-amber-800",
};

const emptyForm = {
  first_name: "", other_names: "", last_name: "",
  admission_no: "", gender: "male", date_of_birth: "",
  phone: "", address: "", blood_group: "",
  // Multi-program enrollments: array of {program_id, class_id, arm_id}
  enrollments: [{ program_id: "", class_id: "", arm_id: "" }],
  // Guardian: either pick existing or fill new
  guardian: { mode: "new", id: null, full_name: "", relationship: "", phone: "", phone_alt: "", email: "" },
};

const ALL = "all";
const createEmptyFilter = (sessionId = ALL) => ({ q: "", program_id: ALL, class_id: ALL, arm_id: ALL, gender: ALL, status: ALL, session_id: sessionId });

const Students = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState(() => createEmptyFilter());
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const dq = useDebounce(filter.q, 250);

  const canBulkCreate =
    hasEffectiveCapability("student.create");
  const canCreateAdmission =
    hasEffectiveCapability("student.create") &&
    hasEffectiveCapability("guardian.manage") &&
    hasEffectiveCapability("student.manage_enrollment");
  const canArchive =
    hasEffectiveCapability("student.archive");

  // ----- Cascading filter sources -----
  const filterProgram = programs.find(p => p.id === filter.program_id);
  const filterClasses = filterProgram?.classes || [];
  const filterClass = filterClasses.find(c => c.id === filter.class_id);
  const filterArms = filterClass?.arms || [];

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filter.q) n++;
    ["program_id","class_id","arm_id","gender","status","session_id"].forEach(k => { if (filter[k] !== ALL) n++; });
    return n;
  }, [filter]);

  const load = async () => {
    if (!sessionsLoaded) return;
    setLoading(true);
    try {
      const params = {};
      if (dq.trim()) params.q = dq.trim();
      if (filter.program_id !== ALL) params.program_id = filter.program_id;
      if (filter.class_id !== ALL) params.class_id = filter.class_id;
      if (filter.arm_id !== ALL) params.arm_id = filter.arm_id;
      if (filter.gender !== ALL) params.gender = filter.gender;
      if (filter.status !== ALL) params.status = filter.status;
      if (filter.session_id !== ALL) params.session_id = filter.session_id;
      const { data } = await api.get("/students", { params });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadPrograms = async () => {
    try {
      const { data } = await api.get("/programs", { params: { active_only: true } });
      const enriched = await Promise.all(data.map(async (p) => {
        const cls = (await api.get("/classes", { params: { program_id: p.id } })).data;
        return { ...p, classes: cls };
      }));
      setPrograms(enriched);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadSessions = async () => {
    try {
      const { data } = await api.get("/academic-sessions");
      const currentSession = data.find((session) => session.is_current);
      setSessions(data);
      if (!currentSession) return toast.error("No current academic session is configured");
      setFilter((previous) => previous.session_id === ALL ? { ...previous, session_id: currentSession.id } : previous);
      setSessionsLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadPrograms(); loadSessions(); /* eslint-disable-next-line */ }, []);
  // Re-fetch whenever any filter changes (debounced q already handled)
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sessionsLoaded, dq, filter.program_id, filter.class_id, filter.arm_id, filter.gender, filter.status, filter.session_id]);

  // When program changes, reset class+arm; when class changes, reset arm
  useEffect(() => { setFilter(f => ({ ...f, class_id: ALL, arm_id: ALL })); /* eslint-disable-next-line */ }, [filter.program_id]);
  useEffect(() => { setFilter(f => ({ ...f, arm_id: ALL })); /* eslint-disable-next-line */ }, [filter.class_id]);
  useEffect(() => { setFilter(f => ({ ...f, class_id: ALL, arm_id: ALL })); /* eslint-disable-next-line */ }, [filter.session_id]);

  const clearFilters = () => setFilter(createEmptyFilter(sessions.find((session) => session.is_current)?.id || ALL));

  const submit = async () => {
    if (!form.first_name?.trim() || !form.last_name?.trim()) return toast.error("First name and last name are required");
    try {
      const payload = {
        first_name: form.first_name, other_names: form.other_names, last_name: form.last_name,
        admission_no: form.admission_no || undefined,
        gender: form.gender, date_of_birth: form.date_of_birth || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        blood_group: form.blood_group || undefined,
      };
      const { data: student } = await api.post("/students", payload);

      // Resolve guardian: pick existing OR create new (with server-side dedupe)
      let guardianId = null;
      const g = form.guardian || {};
      if (g.mode === "existing" && g.id) {
        guardianId = g.id;
      } else if (g.full_name?.trim() || g.phone || g.email) {
        try {
          const { data: gres } = await api.post("/guardians", {
            full_name: g.full_name?.trim() || "—",
            relationship: g.relationship || undefined,
            phone: g.phone || undefined,
            phone_alt: g.phone_alt || undefined,
            email: g.email || undefined,
          });
          guardianId = gres.guardian.id;
        } catch (e) { toast.error("Guardian create failed: " + formatApiError(e)); }
      }
      if (guardianId) {
        try {
          await api.post(`/students/${student.id}/guardians`, {
            guardian_id: guardianId, relationship: g.relationship || undefined, is_primary: true,
          });
        } catch (e) { toast.error("Guardian link failed: " + formatApiError(e)); }
      }

      // Create each completed enrollment row (program + class required; arm optional)
      const completed = (form.enrollments || []).filter(e => e.program_id && e.class_id);
      const enrolled = [];
      const failures = [];
      for (const e of completed) {
        try {
          await api.post(`/students/${student.id}/enrollments`, {
            program_id: e.program_id, class_id: e.class_id, arm_id: e.arm_id || undefined,
          });
          enrolled.push(programs.find(p => p.id === e.program_id)?.name || "Program");
        } catch (err) { failures.push(formatApiError(err)); }
      }
      let msg = `Admitted ${student.full_name} (${student.admission_no})`;
      if (enrolled.length > 0) msg += ` · ${enrolled.length} program${enrolled.length > 1 ? "s" : ""}`;
      if (failures.length > 0) toast.error("Some enrollments failed: " + failures.join("; "));
      toast.success(msg);
      setOpen(false); setForm(emptyForm); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Enrollment row helpers
  const addEnrollment = () => setForm(f => ({ ...f, enrollments: [...(f.enrollments || []), { program_id: "", class_id: "", arm_id: "" }] }));
  const removeEnrollment = (idx) => setForm(f => ({ ...f, enrollments: f.enrollments.filter((_, i) => i !== idx) }));
  const updateEnrollment = (idx, patch) => setForm(f => ({
    ...f,
    enrollments: f.enrollments.map((e, i) => i === idx ? { ...e, ...patch } : e),
  }));

  const remove = async (id) => {
    if (!window.confirm("Delete this student?")) return;
    try {
      await api.delete(`/students/${id}`);
      setList((l) => l.filter((s) => s.id !== id));
      toast.success("Student removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="students-page">
      <PageHeader
        overline="Admissions"
        title="Students"
        description="Multi-program enrollment · One student, many programs (Conventional, Creche, Islamiyyah, Tahfeez)."
        actions={
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
              <Input
                value={filter.q}
                onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                placeholder="Live search · name or DFA-2026-…"
                className="pl-9 h-10 w-72 bg-white"
                data-testid="students-search"
              />
              {filter.q && (
                <button onClick={() => setFilter({ ...filter, q: "" })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1B2C5A]" aria-label="Clear search">
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {canBulkCreate && (
              <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-[#E2E8F0]" data-testid="bulk-student-upload-btn">
                <Upload className="w-4 h-4 mr-1.5" /> Bulk Upload
              </Button>
            )}
            {canCreateAdmission && (
              <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(emptyForm); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="add-student-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> New Admission
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display text-[#1B2C5A]">New Admission</DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <Field label="First Name" required><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="student-first-name" /></Field>
                    <Field label="Other Names"><Input value={form.other_names} onChange={(e) => setForm({ ...form, other_names: e.target.value })} placeholder="Middle / additional names" /></Field>
                    <Field label="Last Name (Surname)" required><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="student-last-name" /></Field>
                    <Field label="Admission No"><Input value={form.admission_no} onChange={(e) => setForm({ ...form, admission_no: e.target.value })} placeholder="Auto · DFA-2026-NNNN" /></Field>
                    <Field label="Gender">
                      <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                        <SelectTrigger data-testid="student-form-gender"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Date of Birth"><Input type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></Field>

                    <div className="col-span-2 mt-2 p-4 rounded-lg bg-[#9DCD3F]/8 border border-[#9DCD3F]/30">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-[#1B2C5A]">
                          <GraduationCap className="w-4 h-4 text-[#9DCD3F]" /> Program Enrollments
                        </div>
                        <span className="text-xs text-[#475569]">Add one or more programs. None is also OK — you can enroll later.</span>
                      </div>
                      <div className="space-y-3">
                        {form.enrollments.map((e, idx) => {
                          const used = form.enrollments.map((x, j) => j !== idx ? x.program_id : null).filter(Boolean);
                          const availablePrograms = programs.filter(p => !used.includes(p.id));
                          const selectedProgram = programs.find(p => p.id === e.program_id);
                          const classOptions = selectedProgram?.classes || [];
                          const selectedClass = classOptions.find(c => c.id === e.class_id);
                          const armOptions = selectedClass?.arms || [];
                          return (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`enrollment-row-${idx}`}>
                              <div className="col-span-4">
                                <Label className="text-[#1B2C5A] text-xs">Program</Label>
                                <Select value={e.program_id} onValueChange={(v) => updateEnrollment(idx, { program_id: v, class_id: "", arm_id: "" })}>
                                  <SelectTrigger className="h-9 bg-white" data-testid={`enrollment-program-${idx}`}><SelectValue placeholder="Pick program" /></SelectTrigger>
                                  <SelectContent>
                                    {availablePrograms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-4">
                                <Label className="text-[#1B2C5A] text-xs">Class</Label>
                                <Select value={e.class_id} onValueChange={(v) => updateEnrollment(idx, { class_id: v, arm_id: "" })} disabled={!e.program_id}>
                                  <SelectTrigger className="h-9 bg-white" data-testid={`enrollment-class-${idx}`}><SelectValue placeholder={e.program_id ? "Pick class" : "—"} /></SelectTrigger>
                                  <SelectContent>
                                    {classOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-3">
                                <Label className="text-[#1B2C5A] text-xs">Arm</Label>
                                <Select value={e.arm_id} onValueChange={(v) => updateEnrollment(idx, { arm_id: v })} disabled={!e.class_id}>
                                  <SelectTrigger className="h-9 bg-white" data-testid={`enrollment-arm-${idx}`}><SelectValue placeholder={e.class_id ? "Arm (optional)" : "—"} /></SelectTrigger>
                                  <SelectContent>
                                    {armOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-1">
                                {form.enrollments.length > 1 && (
                                  <Button type="button" size="icon" variant="ghost" onClick={() => removeEnrollment(idx)} className="text-red-700 h-9 w-9" data-testid={`enrollment-remove-${idx}`} title="Remove">
                                    <XIcon className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {form.enrollments.length < programs.length && (
                        <Button type="button" size="sm" variant="outline" onClick={addEnrollment} className="mt-3 border-[#9DCD3F] text-[#1B2C5A]" data-testid="enrollment-add">
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add another program
                        </Button>
                      )}
                    </div>

                    <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                    <Field label="Blood Group"><Input value={form.blood_group} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} placeholder="O+" /></Field>
                    <div className="col-span-2"><Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>

                    <div className="col-span-2 mt-2 p-4 rounded-lg bg-[#FDFBF7] border border-[#E2E8F0]">
                      <div className="text-sm font-medium text-[#1B2C5A] mb-2 inline-flex items-center gap-1.5">
                        <span className="w-1 h-4 bg-[#9DCD3F] rounded-full" /> Guardian / Parent
                      </div>
                      <GuardianPicker value={form.guardian} onChange={(v) => setForm({ ...form, guardian: v })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="student-form-submit">Admit Student</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </>
        }
      />

      {/* Filter Bar */}
      <Card className="border-[#E2E8F0] mb-4 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-[#475569] mr-1">
            <Filter className="w-3.5 h-3.5 text-[#9DCD3F]" /> Filters
          </div>
          <FilterSelect testid="filter-program" value={filter.program_id} onChange={(v) => setFilter(f => ({ ...f, program_id: v }))} placeholder="All Programs"
            options={programs.map(p => ({ value: p.id, label: p.name }))} />
          <FilterSelect testid="filter-class" value={filter.class_id} onChange={(v) => setFilter(f => ({ ...f, class_id: v }))} placeholder="All Classes" disabled={filter.program_id === ALL}
            options={filterClasses.map(c => ({ value: c.id, label: c.name }))} />
          <FilterSelect testid="filter-arm" value={filter.arm_id} onChange={(v) => setFilter(f => ({ ...f, arm_id: v }))} placeholder="All Arms" disabled={filter.class_id === ALL}
            options={filterArms.map(a => ({ value: a.id, label: a.name }))} />
          <FilterSelect testid="filter-gender" value={filter.gender} onChange={(v) => setFilter(f => ({ ...f, gender: v }))} placeholder="Any Gender"
            options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]} />
          <FilterSelect testid="filter-status" value={filter.status} onChange={(v) => setFilter(f => ({ ...f, status: v }))} placeholder="Any Status"
            options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "graduated", label: "Graduated" }, { value: "withdrawn", label: "Withdrawn" }]} />
          {sessions.length > 0 && (
            <FilterSelect testid="filter-session" value={filter.session_id} onChange={(v) => setFilter(f => ({ ...f, session_id: v }))} placeholder="Any Session"
              options={sessions.map(s => ({ value: s.id, label: s.name + (s.is_current ? " (current)" : "") }))} />
          )}
          <div className="ml-auto flex items-center gap-3">
            <div className="text-xs text-[#475569]" data-testid="students-result-count">
              {loading ? "Searching…" : <><strong className="text-[#1B2C5A]">{list.length}</strong> result{list.length !== 1 ? "s" : ""}</>}
              {activeFilterCount > 0 && !loading && <span className="text-[#9DCD3F]"> · {activeFilterCount} filter{activeFilterCount !== 1 ? "s" : ""} active</span>}
            </div>
            {activeFilterCount > 0 && (
              <Button size="sm" variant="ghost" onClick={clearFilters} className="text-[#1B2C5A] hover:text-red-700" data-testid="clear-filters-btn">
                <XIcon className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      {list.length === 0 ? (
        <EmptyState message="No students yet. Add your first admission." testId="students-empty" />
      ) : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead>Student</TableHead>
                <TableHead>Admission No.</TableHead>
                <TableHead>Conventional</TableHead>
                <TableHead>Other Programs</TableHead>
                <TableHead>Gender</TableHead>
                <TableHead>Guardian</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((s) => {
                const conv = (s.programs || []).find(p => p.program_code === "CONV");
                const others = (s.programs || []).filter(p => p.program_code !== "CONV");
                return (
                  <TableRow key={s.id} data-testid={`student-row-${s.id}`}>
                    <TableCell>
                      <button onClick={() => navigate(`/app/students/${s.id}`)} className="flex items-center gap-2.5 text-left group">
                        {s.photo ? (
                          <img src={s.photo} alt={s.full_name} className="w-9 h-9 rounded-full object-cover border border-[#E2E8F0] shrink-0" loading="lazy" />
                        ) : (
                          <span className="w-9 h-9 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-medium shrink-0">
                            {(s.first_name?.[0] || s.full_name?.[0] || "?").toUpperCase()}{(s.last_name?.[0] || "").toUpperCase()}
                          </span>
                        )}
                        <span className="font-medium text-[#1B2C5A] group-hover:text-[#9DCD3F] truncate">{s.full_name}</span>
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.admission_no}</TableCell>
                    <TableCell>
                      {conv ? (
                        <span className="text-sm font-medium text-[#1B2C5A]">{conv.class_name}{conv.arm_name ? ` ${conv.arm_name}` : ""}</span>
                      ) : <span className="text-xs text-[#475569]">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {others.length > 0 ? others.map(p => (
                          <span key={p.enrollment_id} className={"text-[10px] px-2 py-0.5 rounded-full " + (PROGRAM_COLORS[p.program_code] || "bg-slate-100 text-slate-700")}>
                            {p.program_code} · {p.class_name}{p.arm_name ? ` ${p.arm_name}` : ""}
                          </span>
                        )) : <span className="text-xs text-[#475569]">—</span>}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{s.gender}</TableCell>
                    <TableCell className="text-sm">{s.guardian_name || "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/app/students/${s.id}`)} data-testid={`student-view-${s.id}`}><Eye className="w-4 h-4" /></Button>
                        {canArchive && (
                          <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="text-red-700 hover:text-red-800" data-testid={`student-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <StudentBulkUpload open={bulkOpen} onOpenChange={setBulkOpen} onComplete={load} />
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[#1B2C5A] text-sm">{label}{required && <span className="text-red-600 ml-1">*</span>}</Label>
    {children}
  </div>
);

const FilterSelect = ({ testid, value, onChange, placeholder, options, disabled }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-9 w-auto min-w-[140px] text-xs bg-white" data-testid={testid}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{placeholder}</SelectItem>
      {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export default Students;
